import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useSSE } from '../hooks/useSSE';
import { toast } from 'sonner';
import { QuoteDialog } from './inbox/QuoteDialog';
import { NewMessageDialog } from './inbox/NewMessageDialog';
import { ConversationList } from './inbox/ConversationList';
import { MessageThread } from './inbox/MessageThread';

export default function Inbox({ isArtistView = false, onUnreadChange }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [peerTyping, setPeerTyping] = useState(false);
  const peerTypingTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const selectedConvIdRef = useRef(null);

  // Keep a ref to the currently open conversation ID so the SSE handler
  // (declared once) can compare without re-subscribing on every selection.
  useEffect(() => { selectedConvIdRef.current = selectedConversation?.id || null; }, [selectedConversation?.id]);

  // Quote state
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quotePrice, setQuotePrice] = useState('');
  const [quoteDescription, setQuoteDescription] = useState('');
  const [quoteBuyerMessage, setQuoteBuyerMessage] = useState('');
  const [quoteDeliveryDays, setQuoteDeliveryDays] = useState('7');
  const [sendingQuote, setSendingQuote] = useState(false);
  const [processingQuoteId, setProcessingQuoteId] = useState(null);

  // New message dialog state
  const [newMsgOpen, setNewMsgOpen] = useState(false);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [startingChat, setStartingChat] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const computeUnreadCount = useCallback((convs) => {
    const count = convs.filter(c => c.unread_count > 0).length;
    onUnreadChange?.(count);
    return count;
  }, [onUnreadChange]);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await api.getConversations();
      setConversations(response.data);
      computeUnreadCount(response.data);
      window.dispatchEvent(new Event('refreshBadges'));
      const convId = searchParams.get('conversation');
      if (convId) {
        const conv = response.data.find(c => c.id === convId);
        if (conv) setSelectedConversation(conv);
      }
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [searchParams, computeUnreadCount]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  useEffect(() => {
    if (!selectedConversation) return;
    const fetchMessages = async () => {
      setLoadingMessages(true);
      try {
        const response = await api.getConversationMessages(selectedConversation.id);
        setMessages(response.data);
        await api.markConversationRead(selectedConversation.id);
        setConversations(prev => {
          const updated = prev.map(c =>
            c.id === selectedConversation.id ? { ...c, unread_count: 0 } : c
          );
          computeUnreadCount(updated);
          return updated;
        });
        window.dispatchEvent(new Event('refreshBadges'));
      } catch (error) {
        toast.error('Failed to load messages');
      } finally {
        setLoadingMessages(false);
      }
    };
    fetchMessages();
  }, [selectedConversation, computeUnreadCount]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (!selectedConversation) return;
    const interval = setInterval(async () => {
      try {
        const response = await api.getConversationMessages(selectedConversation.id);
        if (response.data.length !== messages.length) setMessages(response.data);
      } catch (error) { console.error('Failed to poll messages:', error); }
    }, 8000);
    return () => clearInterval(interval);
  }, [selectedConversation, messages.length]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const response = await api.getConversations();
        setConversations(response.data);
        computeUnreadCount(response.data);
      } catch (error) { console.error('Failed to poll conversations:', error); }
    }, 15000);
    return () => clearInterval(interval);
  }, [computeUnreadCount]);

  // Real-time: read receipts + typing indicators via existing SSE stream.
  useSSE(useCallback((event) => {
    const convId = selectedConvIdRef.current;
    if (!event || event.conversation_id !== convId) return;

    if (event.type === 'messages_read') {
      // Peer just opened this conversation — flip our own bubbles to "Seen".
      setMessages(prev => prev.map(m => (
        m.sender_id === user?.id && !m.is_read
          ? { ...m, is_read: true, read_at: event.read_at }
          : m
      )));
    } else if (event.type === 'typing') {
      if (event.sender_id === user?.id) return; // ignore our own echoes
      if (event.is_typing) {
        setPeerTyping(true);
        if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
        // Auto-clear if we stop getting typing pings (peer stopped or lost connection)
        peerTypingTimerRef.current = setTimeout(() => setPeerTyping(false), 4000);
      } else {
        if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
        setPeerTyping(false);
      }
    } else if (event.type === 'new_message') {
      // Peer sent us a new message on the OPEN conversation — pull latest
      // list so it shows without waiting for the 8s poll.
      api.getConversationMessages(convId)
        .then(r => setMessages(r.data))
        .catch(() => {});
    }
  }, [user?.id]));

  // Reset typing indicator when switching conversations.
  useEffect(() => {
    setPeerTyping(false);
    if (peerTypingTimerRef.current) { clearTimeout(peerTypingTimerRef.current); peerTypingTimerRef.current = null; }
  }, [selectedConversation?.id]);

  // ==================== HANDLERS ====================

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
    setSearchParams({ tab: 'inbox', conversation: conv.id });
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
    setMessages([]);
    setSearchParams({ tab: 'inbox' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || !selectedConversation) return;
    setSending(true);
    try {
      const response = await api.sendDirectMessage(selectedConversation.id, newMessage.trim());
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      setConversations(prev => prev.map(c =>
        c.id === selectedConversation.id
          ? { ...c, last_message: newMessage.trim().slice(0, 100), last_message_at: new Date().toISOString(), last_message_sender: isArtistView ? 'artist' : 'user' }
          : c
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSendQuote = async () => {
    if (!quotePrice || !quoteDescription.trim()) { toast.error('Fill in price and description'); return; }
    const price = parseFloat(quotePrice);
    if (isNaN(price) || price <= 0) { toast.error('Enter a valid price'); return; }
    setSendingQuote(true);
    try {
      const response = await api.sendQuote(selectedConversation.id, price, quoteDescription.trim(), quoteBuyerMessage.trim(), parseInt(quoteDeliveryDays) || 7);
      setMessages(prev => [...prev, response.data]);
      setQuoteDialogOpen(false);
      toast.success('Quote sent!');
      setConversations(prev => prev.map(c =>
        c.id === selectedConversation.id
          ? { ...c, last_message: `Sent a quote: $${price.toFixed(2)}`, last_message_at: new Date().toISOString(), last_message_sender: 'artist', has_pending_quote: true }
          : c
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quote');
    } finally {
      setSendingQuote(false);
    }
  };

  const handleAcceptQuote = async (msg) => {
    setProcessingQuoteId(msg.id);
    try {
      const response = await api.acceptQuote(msg.id);
      if (response.data.checkout_url) window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept quote');
      setProcessingQuoteId(null);
    }
  };

  const handleDeclineQuote = async (messageId) => {
    try {
      await api.declineQuote(messageId);
      setMessages(prev => prev.map(msg =>
        msg.id === messageId ? { ...msg, quote_data: { ...msg.quote_data, status: 'declined' } } : msg
      ));
      toast.success('Quote declined');
      setConversations(prev => prev.map(c =>
        c.id === selectedConversation.id ? { ...c, has_pending_quote: false } : c
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline quote');
    }
  };

  const sortByDate = (arr, key, asc = true) =>
    [...arr].sort((a, b) => (asc ? 1 : -1) * (new Date(a[key] || a.created_at) - new Date(b[key] || b.created_at)));

  const optimisticDelete = (removeFn, restoreFn, apiCall, successMsg) => {
    removeFn();
    const timer = setTimeout(() => {
      apiCall().catch(() => { restoreFn(); toast.error(`Failed to delete ${successMsg}`); });
    }, 5000);
    toast(`${successMsg} deleted`, {
      action: { label: 'Undo', onClick: () => { clearTimeout(timer); restoreFn(); } },
      duration: 5000
    });
  };

  const handleDeleteConversation = (e, convId) => {
    e.stopPropagation();
    const deleted = conversations.find(c => c.id === convId);
    const wasSelected = selectedConversation?.id === convId;
    optimisticDelete(
      () => {
        setConversations(prev => prev.filter(c => c.id !== convId));
        if (wasSelected) { setSelectedConversation(null); setMessages([]); setSearchParams({ tab: 'inbox' }); }
      },
      () => setConversations(prev => sortByDate([...prev, deleted], 'last_message_at', false)),
      () => api.deleteConversation(convId),
      'Conversation'
    );
  };

  const handleDeleteMessage = (msgId) => {
    if (!selectedConversation) return;
    const deleted = messages.find(m => m.id === msgId);
    const convId = selectedConversation.id;
    optimisticDelete(
      () => setMessages(prev => prev.filter(m => m.id !== msgId)),
      () => setMessages(prev => sortByDate([...prev, deleted], 'created_at')),
      () => api.deleteMessage(convId, msgId),
      'Message'
    );
  };

  const handleOpenNewMessage = async () => {
    setNewMsgOpen(true);
    setArtistSearch('');
    setLoadingArtists(true);
    try {
      const res = await api.getArtists();
      setArtistResults((res.data || []).filter(a => a.user_id !== user?.id));
    } catch (error) {
      toast.error('Failed to load artists');
    }
    setLoadingArtists(false);
  };

  const handleStartNewConversation = async (artistId) => {
    setStartingChat(artistId);
    try {
      const res = await api.startConversation(artistId);
      setNewMsgOpen(false);
      await fetchConversations();
      setSelectedConversation(res.data);
      setSearchParams({ tab: 'inbox', conversation: res.data.id });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start conversation');
    }
    setStartingChat(null);
  };

  const filteredArtists = artistResults.filter(a =>
    a.name.toLowerCase().includes(artistSearch.toLowerCase())
  );

  // ==================== SUB-RENDERS ====================

  const renderConversationList = (className = '') => (
    <ConversationList
      className={className}
      conversations={conversations}
      selectedConversation={selectedConversation}
      isArtistView={isArtistView}
      loading={loading}
      onSelectConversation={handleSelectConversation}
      onDeleteConversation={handleDeleteConversation}
      onOpenNewMessage={handleOpenNewMessage}
    />
  );

  const messageThreadElement = (
    <MessageThread
      selectedConversation={selectedConversation}
      isArtistView={isArtistView}
      messages={messages}
      loadingMessages={loadingMessages}
      messagesEndRef={messagesEndRef}
      newMessage={newMessage}
      setNewMessage={setNewMessage}
      handleSendMessage={handleSendMessage}
      sending={sending}
      handleBackToList={handleBackToList}
      handleAcceptQuote={handleAcceptQuote}
      handleDeclineQuote={handleDeclineQuote}
      processingQuoteId={processingQuoteId}
      handleDeleteMessage={handleDeleteMessage}
      setQuotePrice={setQuotePrice}
      setQuoteDescription={setQuoteDescription}
      setQuoteBuyerMessage={setQuoteBuyerMessage}
      setQuoteDeliveryDays={setQuoteDeliveryDays}
      setQuoteDialogOpen={setQuoteDialogOpen}
      peerTyping={peerTyping}
    />
  );

  // ==================== MAIN LAYOUT ====================
  return (
    <div className="bg-surface border border-[#262626] rounded-xl overflow-hidden" data-testid="inbox-container">
      <div className="hidden lg:flex h-[600px]">
        {renderConversationList('w-[300px] border-r border-[#262626] flex-shrink-0')}
        {messageThreadElement}
      </div>
      <div className="lg:hidden h-[600px]">
        {selectedConversation ? <div className="flex flex-col h-full">{messageThreadElement}</div> : renderConversationList('h-full')}
      </div>

      <QuoteDialog
        open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}
        quotePrice={quotePrice} setQuotePrice={setQuotePrice}
        quoteDeliveryDays={quoteDeliveryDays} setQuoteDeliveryDays={setQuoteDeliveryDays}
        quoteDescription={quoteDescription} setQuoteDescription={setQuoteDescription}
        quoteBuyerMessage={quoteBuyerMessage} setQuoteBuyerMessage={setQuoteBuyerMessage}
        sendingQuote={sendingQuote} onSendQuote={handleSendQuote}
        recipientName={selectedConversation?.user_name}
      />

      <NewMessageDialog
        open={newMsgOpen} onOpenChange={setNewMsgOpen}
        artistSearch={artistSearch} setArtistSearch={setArtistSearch}
        filteredArtists={filteredArtists} loadingArtists={loadingArtists}
        startingChat={startingChat} onStartConversation={handleStartNewConversation}
      />
    </div>
  );
}
